import {
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  PageHeader,
  Button,
  Form,
  Input,
  Select,
  notification,
  Table,
  Tooltip,
  Row,
  Col,
} from "antd";
import { useEffect, useState } from "react";
import IconNotFound from "../../static/icon/IconNotFound";
import IconWebAdmin from "../../static/icon/IconWebAmin";
import DeleteAction from "../../Tools/DeleteAction";
import ActionModal from "./ActionModal";

import { useAuth } from "../../context/auth";
import { formatAdminRole, listAdminRole } from "../../utils/formatter";
import MerchantDropdown from "../Dropdown/MerchantDropdown";
import LandlordDropdown from "../Dropdown/LandlordDropdown";
import PartyDropdown from "../Dropdown/PartyDropdown";

const WebAdminUser = () => {
  const { accessToken } = useAuth();

  const [form] = Form.useForm();
  const selectedMerchants = Form.useWatch('merchantIds', form);

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState({
    pageValue: {},
    searchValue: {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [modalType, setModalType] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    async function getUsers({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/user', window.location);
      url.searchParams.append('admin', 'true');
      if (searchValue.email) url.searchParams.append('email', searchValue.email);
      if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
      if (searchValue.partyIds) for (const partyId of searchValue.partyIds) url.searchParams.append('partyId', partyId);
      if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
      if (searchValue.adminRole) url.searchParams.append('adminRole', searchValue.adminRole);
      if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);
      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, users } = await res.json();
        setTotal(total);
        setUsers(users);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getUsers(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: () => null,
    },
    {
      title: "Name-Surname",
      dataIndex: "firstName",
      key: "firstName",
      sorter: () => null,
      render: (_, user) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {user.firstName} {user.lastName}
        </span>
      ),
    },
    {
      title: "Role",
      dataIndex: "adminRole",
      key: "adminRole",
      width: 180,
      sorter: () => null,
      render: (adminRole) => formatAdminRole(adminRole),
    },
    {
      title: "Cluster Name",
      dataIndex: "merchantNameEn",
      key: "merchantNameEn",
      sorter: () => null,
    },
    {
      title: "Group Name",
      dataIndex: "partyNameEn",
      key: "partyNameEn",
      sorter: () => null,
    },
    {
      title: "Site Name",
      dataIndex: "landlordNameEn",
      key: "landlordNameEn",
      sorter: () => null,
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: 120,
      sorter: () => null,
      render: (active) => (
        <span style={{ color: active ? "#55B491" : "#EB5757" }}  >
          {active ? 'Available' : 'Locked'}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, user) => (
        <>
          <span style={{ whiteSpace: "nowrap" }}>
            <Tooltip placement="bottom" title="Edit" className="mx-1">
              <EditTwoTone
                style={{
                  backgroundColor: "#F3F6F9",
                  padding: 6,
                  borderRadius: 6,
                }}
                onClick={() => {
                  setModalType("edit");
                  setCurrentUser(user);
                }}
              />
            </Tooltip>
            {!user.active && (
              <Tooltip placement="bottom" title="Delete" className="mx-1">
                <DeleteTwoTone
                  style={{
                    backgroundColor: "#F3F6F9",
                    padding: 6,
                    borderRadius: 6,
                  }}
                  onClick={() => {
                    setModalType("delete");
                    setCurrentUser(user);
                  }}
                  twoToneColor="red"
                />
              </Tooltip>
            )}
          </span>
        </>
      ),
    },
  ];

  const [downloading, setDownloading] = useState(false);
  async function downloadXlsx({ searchValue }) {
    setDownloading(true);

    const url = new URL('/api/user', window.location);
    url.searchParams.append('admin', 'true');
    url.searchParams.append('mode', 'xlsx');

    if (searchValue.email) url.searchParams.append('email', searchValue.email);
    if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
    if (searchValue.partyIds) for (const partyId of searchValue.partyIds) url.searchParams.append('partyId', partyId);
    if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
    if (searchValue.adminRole) url.searchParams.append('adminRole', searchValue.adminRole);
    if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);

    const res = await fetch(url, {
      headers: { 'X-ACCESS-TOKEN': accessToken },
    });
    if (res.ok) {
      const data = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(data);
      a.download = `User-Admin_${new Date().toISOString()}.xlsx`;
      a.click();
    } else {
      if (res.status === 404) {
        notification["error"]({
          message: "No matched user admin found",
        });
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    setDownloading(false);
  }

  return (
    <>
      <div className="flex justify-between items-center mt-2">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconWebAdmin /> &nbsp;&nbsp;Web Admin User
            </span>
          }
        />

        <Button
          className="mt-2 mr-3 flex items-center px-12 border-0"
          loading={downloading}
          type="primary"
          shape="round"
          style={{ background: "#1BC5BD" }}
          onClick={() => downloadXlsx(search)}
        >
          Export
        </Button>

        <Button
          className="mt-2 flex items-center"
          icon={<PlusOutlined />}
          type="primary"
          shape="round"
          onClick={() => {
            setModalType("new");
            setCurrentUser({});
          }}
        >
          New User
        </Button>
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Email"
                  name="email"
                  colon={false}
                  initialValue={""}
                >
                  <Input
                    placeholder="Username"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Cluster Name"
                  name="merchantIds"
                  colon={false}
                  initialValue={[]}
                >
                  <MerchantDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    setSelectedMerchants={(merchantIds) => form.setFieldsValue({ merchantIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Group Name"
                  name="partyIds"
                  colon={false}
                  initialValue={[]}
                >
                  <PartyDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    setSelectedPartys={(partyIds) => form.setFieldsValue({ partyIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Site Name"
                  name="landlordIds"
                  colon={false}
                  initialValue={[]}
                >
                  <LandlordDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    merchantIds={selectedMerchants}
                    setSelectedLandlords={(landlordIds) => form.setFieldsValue({ landlordIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Role"
                  name="adminRole"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Role' },
                      ...listAdminRole.map(adminRole => ({
                        value: adminRole.name,
                        label: adminRole.value,
                      })),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Status"
                  name="active"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Status' },
                      { value: true, label: 'Active' },
                      { value: false, label: 'Inactive' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={8}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>
                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>

      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(user) => user.id}
          loading={loading}
          columns={columns}
          dataSource={users}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <ActionModal
        visible={modalType !== 'delete' && !!currentUser}
        modalType={modalType}
        editRecord={currentUser}
        onCancel={() => setCurrentUser(null)}
        onOk={() => {
          setCurrentUser(null);
          setUpdate(update + 1);
        }}
      />

      <DeleteAction
        openModalActionDelete={modalType === 'delete' && !!currentUser}
        name="Admin User"
        type="user"
        editRecord={currentUser}
        onCancel={() => setCurrentUser(null)}
        onOk={() => {
          setCurrentUser(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default WebAdminUser;
